import { ListItem, QuestionAnswerCard } from '@ninjacart/nc-nk-adaptive-cards';
import {
  createCommentQnA,
  getQnACommentList,
  onQnAReplyLike,
  QnAPlaceHolder,
  useQnaFeedStore,
} from '@ninjacart/nc-nk-advisory';
import { UserProps, useEventZStore } from '@ninjacart/nc-nk-store';
import { Separator, Spinner, useTheme } from '@ninjacart/nc-nk-ui';
import { BannerAds, useIsFocused, useScreenDimensions } from '@ninjacart/nc-nk-web-deps';
import { FlashList } from '@shopify/flash-list';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, View } from 'react-native';
import useUserEngagementStore from '../store/useUserEngagementStore';
import {ProfileTabEmptyState} from './profileTabEmptyState';
type AllTabParams = {
  userProfile?: UserProps;
  id?: string;
  onReportBottomSheetOpen: Function;
};

const ProfileAllTab = ({ userProfile, id, onReportBottomSheetOpen }: AllTabParams) => {
  const userQnaFeed = useUserEngagementStore((state) => state.userQnaFeed);
  const homeFeedsCard = useUserEngagementStore((state) => state.homeFeedsCard);
  const isVideoLoaded = useUserEngagementStore((state) => state.isVideoLoaded);
  const isQnaLoaded = useUserEngagementStore((state) => state.isQnaLoaded);
  const fetchQuestionData = useUserEngagementStore((state) => state.fetchQuestionData);
  const fetchAllVideosData = useUserEngagementStore((state) => state.fetchAllVideosData);
  const resetQnaFeed = useUserEngagementStore((state) => state.resetQnaFeed);
  const resetHomeFeedsCard = useUserEngagementStore((state) => state.resetHomeFeedsCard);
  const screenDimensions = useScreenDimensions();
  const startCountRef = useRef({ page: 1, limit: 10, start: 0, count: 10 });
  const { t } = useTranslation();
  const isFocused = useIsFocused();
  const theme = useTheme();
  const userId = id ?? userProfile?.user?.id!;
  const realmId = userProfile?.realm?.id ?? '1';
  const [refreshing, setRefreshing] = useState(false);
  const [onEndReachedSpinnerVideo, setOnEndReachedSpinnerVideo] = useState(false);
  const bannerAdKeywords = useEventZStore((state) => state.bannerAdKeywords);
  const deviceWidth = Dimensions.get('window').width;

  const qnaPayloadRef = useRef({
    userId: userId!,
    realmId: realmId,
    limit: startCountRef.current.limit,
    page: startCountRef.current.page,
    desc: true,
    includeReplies: true,
  });

  const videoListPayloadRef = useRef({
    userId: userId,
    offset: startCountRef.current.start,
    count: startCountRef.current.count,
  });

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    if (isFocused) {
      startCountRef.current.page = 1;
      fetchQuestionData(qnaPayloadRef.current, false);
      startCountRef.current.start = 0;
      fetchAllVideosData(videoListPayloadRef.current, false);
    }
  }, [isFocused]);

  const sortFeeds = (feeds) => {
    return feeds.sort((a, b) => {
      const dateA = a.actionData?.content?.created_at || a.actionData?.content?.createdAt;
      const dateB = b.actionData?.content?.created_at || b.actionData?.content?.createdAt;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  };

  const allFeedsData = sortFeeds([...homeFeedsCard, ...userQnaFeed]);

  const onEndReachedAllFeeds = async () => {
    setOnEndReachedSpinnerVideo(true);
    try {
      startCountRef.current.start += startCountRef.current.count;
      startCountRef.current.page += 1;

      qnaPayloadRef.current.page = startCountRef.current.page;
      videoListPayloadRef.current.offset = startCountRef.current.start;

      await fetchQuestionData(qnaPayloadRef.current, false);
      await fetchAllVideosData(videoListPayloadRef.current, false);
    } catch (error) {
      console.error('Error fetching more feeds:', error);
    } finally {
      setOnEndReachedSpinnerVideo(false);
    }
  };

  const onPullToRefresh = async () => {
    setRefreshing(true);
    startCountRef.current.start = 0;
    startCountRef.current.page = 1;

    qnaPayloadRef.current.page = 1;
    videoListPayloadRef.current.offset = 0;
    resetQnaFeed();
    resetHomeFeedsCard();
    try {
      await fetchAllVideosData(videoListPayloadRef.current, true);
      await fetchQuestionData(qnaPayloadRef.current, true);
    } catch (err) {
      console.log('err video list onPullToRefreshVideo', err);
    } finally {
      setRefreshing(false);
    }
  };

  const renderItem = ({ item, index }) => {
    switch (item?.type) {
      case 'STREAM':
        return (
          <ListItem
            item={item}
            index={index}
            userProfile={userProfile}
            screenDimensions={screenDimensions}
            onReportBottomSheetOpen={onReportBottomSheetOpen}
            onCommentBottomSheetOpen={() => {}}
            pageName={'Profile Detail'}
            isAutoPlayBannerAdEnabled={false}
          />
        );

      case 'QNA':
        return (
          <QuestionAnswerCard
            item={item?.actionData?.content}
            index={index}
            questionId={''}
            // expertAnswer={item?.actionData?.content?.children?.find((x) => x.creatorRole === 'mods')}
            useQnaFeedStore={useQnaFeedStore}
            createCommentQnA={createCommentQnA}
            getQnACommentList={getQnACommentList}
            onQnAReplyLike={onQnAReplyLike}
          />
        );

      case 'BANNER_AD':
        return (
          <BannerAds
            deviceWidth={deviceWidth}
            itemKey={item?.actionData?.content?.itemKey ?? index.toString()}
            unitId={item?.actionData?.content?.unitId}
            adSizeDefs={item?.actionData?.content?.adSizeDefs ?? []}
            bannerAdKeywords={bannerAdKeywords}
            screenName={'profile detail page'}
          />
        );

      default:
        return null;
    }
  };

  const ListEmptyComponent = () => {
    (isVideoLoaded || isQnaLoaded) && !allFeedsData?.length;
    return <ProfileTabEmptyState />;
  };

  if (!isVideoLoaded || !isQnaLoaded) {
    return <QnAPlaceHolder showDetailPage={true} />;
  }

  return (
    <View style={{ flex: 1, backgroundColor: theme['background']?.val }}>
      <FlashList
        data={allFeedsData}
        renderItem={renderItem}
        keyExtractor={(item, index) => index.toString()}
        getItemType={(item) => item.type}
        estimatedItemSize={450}
        scrollEventThrottle={24}
        showsVerticalScrollIndicator={false}
        onEndReached={onEndReachedAllFeeds}
        refreshing={refreshing}
        onRefresh={onPullToRefresh}
        onEndReachedThreshold={0.8}
        ListFooterComponent={() =>
          onEndReachedSpinnerVideo && (
            <View style={{ flex: 1, padding: 10, alignItems: 'center' }}>
              <Spinner size="large" color="$green10" />
            </View>
          )
        }
        ListFooterComponentStyle={{ height: 100 }}
        ListEmptyComponent={ListEmptyComponent}
        ItemSeparatorComponent={Separator}
      />
    </View>
  );
};

export default ProfileAllTab;
